import {useMemo} from "react";
import {Link} from "react-router";
import {Loader2Icon, Plus} from "lucide-react";
import {createSerializer, parseAsInteger, parseAsString, useQueryState} from "nuqs";
import {List, RowComponentProps} from "react-window";
import {useInfiniteLoader} from "react-window-infinite-loader";
import SearchBar from "@/search/SearchBar.tsx";
import {Button} from "@/components/ui/button.tsx";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {SpellSummary} from "@/spells/model/Spell.ts";
import {SpellFilter as SpellFilterPopover} from "@/spells/admin/SpellFilter.tsx";
import SpellListItem from "@/spells/SpellListItem.tsx";
import {useSpellsQuery} from "@/spells/SpellQueries.ts";
import {isCharacterClass} from "@/characters/model/CharacterClasses.ts";
import {isSpellSchool} from "@/spells/model/SpellSchools.ts";

const PAGE_SIZE = 50;
const searchParsers = {
    name: parseAsString.withDefault(""),
    clazz: parseAsString.withDefault(""),
    school: parseAsString.withDefault(""),
    level: parseAsInteger,
};
const serializeSearch = createSerializer(searchParsers);

export default function SpellListPanel({selectedId}: {selectedId?: string}) {
    const [name, setName] = useQueryState("name", searchParsers.name);
    const [clazz, setClazz] = useQueryState("clazz", searchParsers.clazz);
    const [school, setSchool] = useQueryState("school", searchParsers.school);
    const [level, setLevel] = useQueryState("level", searchParsers.level);
    const {campaignPath} = useCampaign();
    const filter = useMemo(() => ({
        name,
        clazz: isCharacterClass(clazz) ? clazz : undefined,
        school: isSpellSchool(school) ? school : undefined,
        level: level ?? undefined,
    }), [clazz, level, name, school]);
    const query = useSpellsQuery(filter, PAGE_SIZE);
    const search = serializeSearch({name, clazz, school, level});
    const spellBasePath = campaignPath("spells");
    const onRowsRendered = useInfiniteLoader({
        isRowLoaded: index => index < query.spells.length,
        loadMoreRows: query.loadMore,
        rowCount: query.rowCount,
        minimumBatchSize: PAGE_SIZE,
    });

    const resetFilters = () => {
        void setSchool("");
        void setLevel(null);
        void setClazz("");
    };

    return (
        <>
            <div className="flex gap-2 p-1">
                <SearchBar className="grow" value={name} onChange={setName}/>
                <SpellFilterPopover level={level} setLevel={setLevel}
                                    clazz={clazz} setClazz={setClazz}
                                    school={school} setSchool={setSchool}
                                    hasFilters={level !== null || Boolean(clazz || school)} reset={resetFilters}/>
            </div>
            <List className="bg-surface"
                  onRowsRendered={onRowsRendered}
                  rowComponent={VirtualSpellRow}
                  rowCount={query.rowCount}
                  rowHeight={40}
                  rowProps={{spells: query.spells, selectedId, spellBasePath, search}}/>
            <div className="flex items-center justify-between gap-2 px-2 py-1">
                <span className="text-sm text-muted-foreground">
                    {query.isError ? "Zauber konnten nicht geladen werden" : `${query.totalCount} Ergebnisse`}
                </span>
                <Button size="sm" asChild>
                    <Link to={{pathname: campaignPath("spells/new"), search}}>Zauber <Plus/></Link>
                </Button>
            </div>
        </>
    );
}

function VirtualSpellRow({index, style, spells, selectedId, spellBasePath, search}: RowComponentProps<{
    spells: SpellSummary[];
    selectedId?: string;
    spellBasePath: string;
    search: string;
}>) {
    const spell = spells[index];
    if (!spell) return <div style={style} className="p-3 text-muted-foreground"><Loader2Icon className="animate-spin"/></div>;
    return <SpellListItem spell={spell}
                          href={`${spellBasePath}/${spell.id}`}
                          search={search}
                          selected={spell.id === selectedId}
                          style={style}/>;
}
